import Dexie, { type EntityTable } from "dexie";
import {
  ChatEntity,
  FileAttachmentEntity,
  LinkEntity,
  MediaAttachmentEntity,
  MessageEntity,
  StoredEpochPayload,
  UserEntity,
  VoiceAttachmentEntity,
} from "./types";
import { openLocalDBWithRecovery, deleteDbWithTimeout } from "./dbOpen";

export { LocalDbOpenError, isLocalDbOpenError } from "./dbOpen";

/** Name of the single local Dexie database. */
export const LOCAL_DB_NAME = "odb";

/**
 * Local IndexedDB schema definition for the "odb" database.
 *
 * Purpose:
 * - Centralize all Dexie schema versions and their data migrations.
 * - Provide a single, well-documented place for evolving local storage.
 *
 * How versioning works (Dexie basics):
 * - Each `db.version(N)` call defines the schema for a specific version.
 * - When the app upgrades, Dexie automatically runs all intermediate
 *   versions, in order, until it reaches the latest one.
 * - Once a version has shipped, its schema definition must be treated as
 *   immutable: do not change indexes or store definitions for old versions.
 *
 * Current versions:
 * - v4: First thread-aware messages schema.
 *   - Adds `threadId` and `[chatId+threadId+timestamp]` compound index.
 *   - No data migration: purely a structural/index update.
 *
 * - v5: Introduces the `pinnedMessages` store.
 *   - Schema-only change; existing pinned messages were not backfilled here.
 *   - Backfilling is intentionally handled in v6 so that both v4->v6 and
 *     v5->v6 upgrade paths share the same migration logic.
 *
 * - v6: Backfills the `pinnedMessages` store from `messages`.
 *   - Iterates over the `messages` table and copies every message where
 *     `isPinned === true` into the `pinnedMessages` store.
 *   - Uses `bulkPut`, so it is idempotent: it can safely run even if some
 *     pinned messages already exist in the `pinnedMessages` store.
 *   - This fixes the issue where existing users did not see pinned messages
 *     after the `pinnedMessages` store was introduced.
 *
 * - v7: UUID7 migration – removes timestamp-based indexes.
 *   - Replaces `[chatId+timestamp]` with `[chatId+id]` compound indexes.
 *   - Replaces `[chatId+threadId+timestamp]` with `[chatId+threadId+id]`.
 *   - Clears both `messages` and `pinnedMessages` tables to force re-fetch
 *     with the new UUID7-based schema and updated entity structure.
 *   - Entity changes include: renamed thread* fields to comment*, added
 *     senderAvatar, changed firstMessageTimestamp to firstMessageId, etc.
 *
 * Maintenance rules for future schema changes:
 * 1. When changing a schema (adding/removing stores or indexes):
 *    - Create a new `db.version(N + 1)` block.
 *    - Copy the full stores definition from the previous version and then
 *      apply your changes only inside the new version.
 *    - Never change the schema string of older versions (4, 5, 6, ...).
 *
 * 2. When you need to migrate existing data:
 *    - Attach an `.upgrade(async (tx) => { ... })` callback to the new
 *      highest version.
 *    - Keep migrations:
 *        - Idempotent (safe to re-run logically, even if Dexie only runs
 *          them once per upgrade path).
 *        - Narrowly scoped: only touch the stores you must modify.
 *        - Backwards-compatible with data from all earlier versions.
 *
 * 3. Adding new stores:
 *    - Add the store definition to the new version only.
 *    - If the store needs to be populated from existing data, do it in the
 *      `.upgrade` of that same version.
 *
 * 4. Removing stores or indexes:
 *    - Consider whether old data must be cleaned up or can be ignored.
 *    - Implement the cleanup logic in the `.upgrade` of the version where
 *      the removal happens.
 *
 * 5. Testing upgrades:
 *    - Always test upgrades across realistic paths, for example:
 *      - Old production tag -> current HEAD.
 *      - The last one or two intermediate releases -> current HEAD.
 *    - Verify at least:
 *      - Schema is created as expected.
 *      - Critical data (e.g., pinned messages) is preserved and readable.
 *
 * If you need to introduce a new version:
 * - Bump the version number by 1.
 * - Update this comment block to document what changed and why.
 * - Add concise inline comments near the new `db.version(...)` explaining
 *   the purpose of the new version and any migration behavior.
 *
 * Example: adding a new version (v8):
 * - Goal: add a new index or store and optionally migrate data.
 * - Steps:
 *   1. Create `db.version(8)`:
 *      - Copy the full `stores` object from v7 into v8.
 *      - Apply your schema change only in v8 (for example, add a new index
 *        or a new store).
 *   2. If existing data must be migrated:
 *      - Attach `.upgrade(async (tx) => { ... })` to v8.
 *      - Use `tx.table("storeName")` to read/write the relevant data.
 *      - Keep the migration logic idempotent and narrowly scoped.
 *   3. Update this JSDoc block with a short description of what v8 does
 *      and why it was added.
 */

// TO DO => add indexes for optimization where needed
// TO DO => consider splitting into multiple dbs if needed
// TO DO => The key MUST be a Number, String, Date or Array instance.
// Booleans are not accepted as valid keys

/** Generic key-value row stored in the `meta` table. */
export type MetaEntry = {
  key: string;
  value: unknown;
};

const db = new Dexie(LOCAL_DB_NAME) as Dexie & {
  // Chats
  chats: EntityTable<ChatEntity, "id">;
  // Messages
  messages: EntityTable<MessageEntity, "id">;
  // Pinned Messages
  pinnedMessages: EntityTable<MessageEntity, "id">;
  // Media Attachments
  mediaAttachments: EntityTable<MediaAttachmentEntity, "id">;
  // File Attachments — download cache and storage-accounting only.
  // The Files tab reads from `messages` directly; this table is written on
  // send (optimistic) and on first download, and read by storage management.
  fileAttachments: EntityTable<FileAttachmentEntity, "id">;
  // Voice Attachments
  voiceAttachments: EntityTable<VoiceAttachmentEntity, "id">;
  // Links
  links: EntityTable<LinkEntity, "id">;
  // Users
  users: EntityTable<UserEntity, "id">;
  // Meta (key-value store for sync cursors, etc.)
  meta: EntityTable<MetaEntry, "key">;
  // Epoch Payloads (warm cache for CSK rotation)
  epochPayloads: EntityTable<StoredEpochPayload, "id">;
};

// Schema declaration:

// v4 – first thread-aware messages schema (no pinned messages yet).
db.version(4).stores({
  chats: "id, type, name, lastMessageTimestamp",
  messages:
    "id, chatId, threadId, timestamp, [chatId+timestamp], [chatId+threadId+timestamp]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
});

// v5 – adds the `pinnedMessages` store (no backfill/migration here).
db.version(5).stores({
  chats: "id, type, name, lastMessageTimestamp",
  messages:
    "id, chatId, threadId, timestamp, [chatId+timestamp], [chatId+threadId+timestamp]",
  pinnedMessages: "id, chatId, timestamp, [chatId+timestamp]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
});

// v6 – backfills the `pinnedMessages` store from `messages`.
db.version(6)
  .stores({
    chats: "id, type, name, lastMessageTimestamp",
    messages:
      "id, chatId, threadId, timestamp, [chatId+timestamp], [chatId+threadId+timestamp]",
    pinnedMessages: "id, chatId, timestamp, [chatId+timestamp]",
    mediaAttachments: "id, chatId",
    fileAttachments: "id, chatId",
    links: "id, chatId",
    users: "id",
  })
  .upgrade(async (tx) => {
    const pinnedMessagesTable = tx.table<MessageEntity, string>(
      "pinnedMessages",
    );
    const messagesTable = tx.table<MessageEntity, string>("messages");
    const pinnedMessages = await messagesTable
      .filter((message) => !!message.isPinned)
      .toArray();

    if (!pinnedMessages.length) {
      return;
    }

    await pinnedMessagesTable.bulkPut(pinnedMessages);
  });

// v7 – UUID7 migration: removes timestamp-based indexes, uses id-based compound indexes
db.version(7)
  .stores({
    chats: "id, type, name, lastMessageTimestamp",
    messages: "id, chatId, threadId, [chatId+id], [chatId+threadId+id]",
    pinnedMessages: "id, chatId, [chatId+id]",
    mediaAttachments: "id, chatId",
    fileAttachments: "id, chatId",
    links: "id, chatId",
    users: "id",
  })
  .upgrade((tx) => {
    // Clear chats, messages, and pinnedMessages to force re-fetch with new UUID7 schema
    // ChatEntity changed: firstMessageTimestamp -> firstMessageId
    // MessageEntity changed: thread* fields renamed to comment*, added senderAvatar, etc.
    return Promise.all([
      tx.table("chats").clear(),
      tx.table("messages").clear(),
      tx.table("pinnedMessages").clear(),
    ]);
  });

// to be continued with v8, v9, ...

db.version(8)
  .stores({
    chats: "id, type, name, lastMessageTimestamp",
    messages:
      "_clientId, id, chatId, threadId, [chatId+id], [chatId+threadId+id]",
    pinnedMessages: "id, chatId, [chatId+id]",
    mediaAttachments: "id, chatId",
    fileAttachments: "id, chatId",
    voiceAttachments: "id, chatId", // NEW
    links: "id, chatId",
    users: "id",
  })
  .upgrade((tx) => {
    return Promise.all([tx.table("messages").clear()]);
  });

// v9 – adds the `hubId` field to the `chats` store
db.version(9)
  .stores({
    chats:
      "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp]",
    messages:
      "_clientId, id, chatId, threadId, [chatId+id], [chatId+threadId+id]",
    pinnedMessages: "id, chatId, [chatId+id]",
    mediaAttachments: "id, chatId",
    fileAttachments: "id, chatId",
    voiceAttachments: "id, chatId",
    links: "id, chatId",
    users: "id",
  })
  .upgrade((tx) => {
    // Add hubId field to existing records
    return tx
      .table("chats")
      .toCollection()
      .modify((chat) => {
        if (!chat.hubId) {
          chat.hubId = null;
        }
      });
  });
// v10 – removes stale `loadingTop` / `loadingBottom` fields from the
// `messages` and `pinnedMessages` stores.
// These were only ever set on pinned messages during initChats and rendered
// as phantom loading dots. Both the writer and reader have been removed;
// this upgrade sweeps any persisted values from existing IndexedDB rows in
// both tables.
db.version(10)
  .stores({
    chats:
      "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp]",
    messages:
      "_clientId, id, chatId, threadId, [chatId+id], [chatId+threadId+id]",
    pinnedMessages: "id, chatId, [chatId+id]",
    mediaAttachments: "id, chatId",
    fileAttachments: "id, chatId",
    voiceAttachments: "id, chatId",
    links: "id, chatId",
    users: "id",
  })
  .upgrade(async (tx) => {
    const clean = (message: Record<string, unknown>) => {
      if ("loadingTop" in message || "loadingBottom" in message) {
        delete message.loadingTop;
        delete message.loadingBottom;
      }
    };

    await Promise.all([
      tx.table("messages").toCollection().modify(clean),
      tx.table("pinnedMessages").toCollection().modify(clean),
    ]);
  });

// v11 – introduces the `decryptionStatus` and `decryptionAttempts` fields on
// `MessageEntity` (and the denormalized copy stored on `ChatEntity.lastMessage`).
// Both fields are optional, so this is a structural-only bump: the upgrade
// callback intentionally does NOT touch existing data. Legacy rows that
// previously persisted "Could not decrypt message" as their content text are
// repaired post-open by `runLegacyDecryptionRepair` (see
// `legacyDecryptionRepair.ts`), which runs once per client outside the
// upgrade transaction so a huge IDB doesn't block the upgrade.
db.version(11).stores({
  chats:
    "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp]",
  messages:
    "_clientId, id, chatId, threadId, [chatId+id], [chatId+threadId+id]",
  pinnedMessages: "id, chatId, [chatId+id]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  voiceAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
});

// v12 – adds a `decryptionStatus` index on `messages` so the retry
// orchestrator can find failed rows via an indexed query
// (`where('decryptionStatus').equals('failed')`) instead of scanning the
// whole table on every trigger (socket connect / chat-list sync /
// route-change). On large IDBs this is meaningfully cheaper.
//
// Schema-only bump — no data migration. Dexie automatically populates the
// new index from existing rows on upgrade.
db.version(12).stores({
  chats:
    "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp]",
  messages:
    "_clientId, id, chatId, threadId, decryptionStatus, [chatId+id], [chatId+threadId+id]",
  pinnedMessages: "id, chatId, [chatId+id]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  voiceAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
});

// v13 – adds the `meta` key-value store for persisting the global sync cursor
// and other lightweight state that needs to survive page reloads.
// Schema-only bump — no data migration. The sync engine initializes the cursor
// on first run.
db.version(13).stores({
  chats:
    "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp]",
  messages:
    "_clientId, id, chatId, threadId, decryptionStatus, [chatId+id], [chatId+threadId+id]",
  pinnedMessages: "id, chatId, [chatId+id]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  voiceAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
  meta: "key",
});

// v14 – introduces the optional `ciphertext` sidecar field on `MessageEntity`
// (raw encrypted `content` + `attachmentMeta`). Rows persisted in a
// not-yet-decryptable state retain their encrypted payload here so the retry
// orchestrator / lazy-decrypt subsystem can re-decrypt from LOCAL bytes
// instead of re-fetching each message from the server — the per-message
// refetch storm this work eliminates. Mirrors the v11 precedent that added
// `decryptionStatus`/`decryptionAttempts`.
//
// Structural-only bump — the field is NOT indexed, so the `stores()` string is
// byte-identical to v13. No `.upgrade()` callback:
//   • Additive optional field → existing rows remain valid without it.
//   • No backfill is possible: legacy `failed` rows already DISCARDED their
//     ciphertext, so there is nothing to populate. Those rows keep relying on
//     the orchestrator's `getMessage` fallback; only rows written after this
//     ships carry `ciphertext`.
//
// ⚠️ No-downgrade story: Dexie/IndexedDB never auto-downgrade. Once a client's
// IDB has opened at v14, shipping code whose highest declared version is 13
// makes Dexie throw `VersionError` on open (the on-disk version exceeds the
// code's max). Because `VITE_USE_NEW_SYNC` is a COMPILE-TIME flag with no
// runtime/remote-config backing, "rolling back" after this ships means
// redeploying a build that still declares v14 (the schema can stay — only the
// sync behaviour is flag-gated), not flipping a flag.
db.version(14).stores({
  chats:
    "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp]",
  messages:
    "_clientId, id, chatId, threadId, decryptionStatus, [chatId+id], [chatId+threadId+id]",
  pinnedMessages: "id, chatId, [chatId+id]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  voiceAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
  meta: "key",
});

// v15 – adds the `[type+lastMessageTimestamp]` compound index on `chats` for
// WS2 Phase 2a window-priority hydration. It lets `computeHydrationWindow`
// (syncWindow.ts) fetch the top-K most-recently-active chats PER list type with
// an indexed reverse cursor + `.limit(K)` — O(window) — instead of loading and
// sorting every row. AI chats already have `[hubId+lastMessageTimestamp]`;
// group/p2p rows carry `hubId=null` (Dexie does not index null, so they are
// absent from that compound index), so this type-keyed index is what makes their
// top-K bounded.
//
// Structural-only bump — a NEW additive index. Dexie auto-populates it from
// existing rows on upgrade, so no `.upgrade()` callback is needed. The index is
// present regardless of the `syncWindowedHydration` feature flag (schema is not
// flag-gated); when the flag is off nothing queries it, so it is inert beyond a
// negligible index-maintenance cost on writes. Same no-downgrade story as v14
// (see that block): rolling back means redeploying a build that still declares
// v15 with the sync BEHAVIOUR flag-gated off — not shipping code capped at v14.
db.version(15).stores({
  chats:
    "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp], [type+lastMessageTimestamp]",
  messages:
    "_clientId, id, chatId, threadId, decryptionStatus, [chatId+id], [chatId+threadId+id]",
  pinnedMessages: "id, chatId, [chatId+id]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  voiceAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
  meta: "key",
});

// v16 – two independent additive changes land together in this version:
//  1. a `sendingStatus` index on `messages` so the startup stranded-send
//     reconciliation (strandedSendReconciliation.ts) can query the few still-
//     PENDING rows directly, instead of a whole-table scan that deserializes
//     every message (incl. multi-KB ciphertext sidecars) — seconds at boot on
//     large accounts, and worse on mobile Safari.
//  2. the `epochPayloads` store: the warm (IndexedDB) cache of self-encapsulated
//     CSKs keyed by (chatId, epoch) for CSK rotation.
//
// Both are structural-only: Dexie auto-populates the new index and creates the
// new store empty on upgrade, so no `.upgrade()` callback is needed. Same
// no-downgrade story as v14/v15: rolling back means redeploying a build that
// still declares v16, not shipping code capped at v15.
db.version(16).stores({
  chats:
    "id, hubId, type, name, lastMessageTimestamp, [hubId+lastMessageTimestamp], [type+lastMessageTimestamp]",
  messages:
    "_clientId, id, chatId, threadId, decryptionStatus, sendingStatus, [chatId+id], [chatId+threadId+id]",
  pinnedMessages: "id, chatId, [chatId+id]",
  mediaAttachments: "id, chatId",
  fileAttachments: "id, chatId",
  voiceAttachments: "id, chatId",
  links: "id, chatId",
  users: "id",
  meta: "key",
  epochPayloads: "id, chatId, [chatId+epoch]",
});

// Initialize the database.
//
// Opens through the hardened recovery path (bounded retry → delete-and-recreate)
// instead of a bare `db.open()`, so WebKit's transient and hard on-disk open
// failures self-heal instead of bricking boot. Rejects with a `LocalDbOpenError`
// only when the DB cannot be opened at all — the caller turns that into an
// actionable recovery screen rather than a logout.
export const initLocalDB = async () => {
  await openLocalDBWithRecovery(db, {
    afterReset: async () => {
      // A destructive reset wiped the attachment metadata rows; drop the OPFS
      // blobs too so they don't orphan and keep consuming the origin quota.
      // Dynamic import (not static) so `db.ts` doesn't pull the OPFS layer —
      // and its `environmentDetector` (which reads window.matchMedia at load) —
      // into the module graph of everything that imports the DB.
      const { clearOPFSAttachments } = await import(
        "@/lib/storage/opfsStorage"
      );
      await clearOPFSAttachments();
    },
  });
};

// Clear all IndexedDB tables on logout.
export const clearLocalDB = () =>
  Promise.all(db.tables.map((table) => table.clear()));

/**
 * Last-resort recovery the UI can invoke when the DB won't open: delete the
 * IndexedDB and its OPFS blobs, then let the caller reload to recreate a fresh
 * schema. Unlike the internal auto-reset, this is explicitly user-initiated, so
 * it bypasses the reset throttle. Safe because the server is the source of
 * truth — local data re-hydrates via sync on the next open.
 */
export const resetLocalDatabase = async (): Promise<void> => {
  try {
    db.close();
  } catch {
    // Not open — nothing to close.
  }
  let deleted = false;
  try {
    // Timeout-capped: a peer connection (e.g. a suspended tab) can leave
    // deleteDatabase blocked and never-settling; without the cap the caller's
    // "Reset local data" button would spin forever.
    await deleteDbWithTimeout(db);
    deleted = true;
  } catch {
    // Ignore (incl. timeout) — a fresh DB is recreated on the next open/reload.
  }
  // Only drop OPFS blobs once the IndexedDB delete is CONFIRMED. If the delete
  // was blocked/timed out the attachment metadata rows survive, so clearing
  // their blobs here would leave rows pointing at deleted content once the DB
  // becomes openable again. Keep both intact and let the reload retry the
  // delete. (The auto-reset path in dbOpen.ts already clears OPFS only after a
  // successful delete+reopen — this keeps the manual path consistent.)
  if (deleted) {
    try {
      // Dynamic import — see initLocalDB's afterReset for the rationale.
      const { clearOPFSAttachments } = await import(
        "@/lib/storage/opfsStorage"
      );
      await clearOPFSAttachments();
    } catch {
      // Best-effort blob cleanup.
    }
  }
};

export { db };
